import { useState, useMemo } from "react";
import { Clock, Star, Trash2, Copy, CheckSquare, Square } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import { useToast } from "@/hooks/use-toast";
import { HistoryFilters } from "./HistoryFilters";
import { BulkActionsBar } from "./BulkActionsBar";
import { EntryManagement } from "./EntryManagement";
import { ImportExportActions } from "./ImportExportActions";
import { HelpDialog } from "./HelpDialog";
import { FeatureTooltip, TOOLTIP_CONTENT } from "./FeaturesTooltips";
import { TranslationEntry } from "@/hooks/useTranslationHistory";


interface TranslationHistoryProps {
  entries: TranslationEntry[];
  onDeleteEntry: (id: string) => void;
  onDeleteMultiple: (ids: string[]) => void;
  onToggleFavorite: (id: string) => void;
  onUpdateEntry: (id: string, updates: Partial<TranslationEntry>) => void;
  onSelectEntry: (entry: TranslationEntry) => void;
  searchHistory: (query: string, filters?: any) => TranslationEntry[];
  exportHistory: (format: 'json' | 'csv') => string;
  importHistory: (data: string, format: 'json' | 'csv') => {
    success: boolean;
    imported: number;
    errors: string[];
  };
}

export function TranslationHistory({
  entries,
  onDeleteEntry,
  onDeleteMultiple,
  onToggleFavorite,
  onUpdateEntry,
  onSelectEntry,
  searchHistory,
  exportHistory,
  importHistory,
}: TranslationHistoryProps) {
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState("history");
  const [searchQuery, setSearchQuery] = useState("");
  const [filters, setFilters] = useState<any>({});
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [showBulkSelection, setShowBulkSelection] = useState(false);

  // Get unique values for filters
  const { availableLanguages, availableCategories, availableTags } = useMemo(() => {
    const languages = new Set<string>();
    const categories = new Set<string>();
    const tags = new Set<string>();

    entries.forEach(entry => {
      languages.add(entry.sourceLanguage);
      languages.add(entry.targetLanguage);
      if (entry.category) categories.add(entry.category);
      entry.tags?.forEach(tag => tags.add(tag));
    });

    return {
      availableLanguages: Array.from(languages),
      availableCategories: Array.from(categories),
      availableTags: Array.from(tags),
    };
  }, [entries]);

  // Filter entries based on search and filters
  const filteredEntries = useMemo(() => {
    if (!searchQuery && Object.keys(filters).length === 0) {
      return entries;
    }
    return searchHistory(searchQuery, filters);
  }, [entries, searchQuery, filters, searchHistory]);

  const historyEntries = filteredEntries.slice(0, 50); // Show more with search
  const favoriteEntries = filteredEntries.filter(entry => entry.isFavorite);

  const handleCopy = async (text: string, type: "source" | "translation") => {
    try {
      await navigator.clipboard.writeText(text);
      toast({
        title: "Copied!",
        description: `${type === "source" ? "Source text" : "Translation"} copied to clipboard`,
        duration: 2000,
      });
    } catch (error) {
      toast({
        title: "Failed to copy",
        description: "Could not copy text to clipboard",
        variant: "destructive",
      });
    }
  };

  const formatLanguage = (code: string) => {
    const languages: Record<string, string> = {
      en: "EN",
      pt: "PT",
      es: "ES",
      fr: "FR",
      de: "DE",
      it: "IT",
      ja: "JA",
      ko: "KO",
      zh: "ZH",
      auto: "Auto"
    };
    return languages[code] || code.toUpperCase();
  };

  const formatTime = (date: Date) => {
    const now = new Date();
    const diffMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));

    if (diffMinutes < 1) return "Just now";
    if (diffMinutes < 60) return `${diffMinutes}m ago`;
    if (diffMinutes < 1440) return `${Math.floor(diffMinutes / 60)}h ago`;
    return date.toLocaleDateString();
  };

  // Bulk actions handlers
  const handleToggleSelection = (entryId: string) => {
    setSelectedIds(prev =>
      prev.includes(entryId)
        ? prev.filter(id => id !== entryId)
        : [...prev, entryId]
    );
  };

  const handleSelectAll = (entries: TranslationEntry[]) => {
    const allIds = entries.map(entry => entry.id);
    setSelectedIds(prev =>
      prev.length === allIds.length ? [] : allIds
    );
  };

  const handleClearSelection = () => {
    setSelectedIds([]);
  };

  const handleBulkDelete = (ids: string[]) => {
    onDeleteMultiple(ids);
    setSelectedIds([]);
  };

  const handleBulkToggleFavorite = (ids: string[], makeFavorite: boolean) => {
    ids.forEach(id => {
      const entry = entries.find(e => e.id === id);
      if (entry && entry.isFavorite !== makeFavorite) {
        onToggleFavorite(id);
      }
    });
    setSelectedIds([]);
  };

  const handleBulkAddTags = (ids: string[], tags: string[]) => {
    ids.forEach(id => {
      const entry = entries.find(e => e.id === id);
      if (entry) {
        const currentTags = entry.tags || [];
        const newTags = [...new Set([...currentTags, ...tags])];
        onUpdateEntry(id, { tags: newTags });
      }
    });
    setSelectedIds([]);
  };

  const handleBulkRemoveTags = (ids: string[], tags: string[]) => {
    ids.forEach(id => {
      const entry = entries.find(e => e.id === id);
      if (entry) {
        const updatedTags = (entry.tags || []).filter(tag => !tags.includes(tag));
        onUpdateEntry(id, { tags: updatedTags });
      }
    });
    setSelectedIds([]);
  };

  const handleBulkSetCategory = (ids: string[], category: string | undefined) => {
    ids.forEach(id => {
      onUpdateEntry(id, { category });
    });
    setSelectedIds([]);
  };

  const EntryCard = ({ entry }: { entry: TranslationEntry }) => (
    <div className="p-3 sm:p-4 bg-translator-panel border border-translator-border rounded-lg hover:shadow-soft transition-smooth group">
      <div className="flex items-start gap-3">
        {/* Selection Checkbox */}
        {showBulkSelection && (
          <div className="flex items-center pt-1">
            <Checkbox
              checked={selectedIds.includes(entry.id)}
              onCheckedChange={() => handleToggleSelection(entry.id)}
            />
          </div>
        )}

        <div
          className="flex-1 cursor-pointer"
          onClick={() => !showBulkSelection && onSelectEntry(entry)}
        >
          <div className="flex items-center gap-2 mb-2">
            <span className="text-xs font-medium text-primary bg-primary/10 px-2 py-1 rounded">
              {formatLanguage(entry.sourceLanguage)} → {formatLanguage(entry.targetLanguage)}
            </span>
            <span className="text-xs text-muted-foreground">
              {formatTime(entry.timestamp)}
            </span>
          </div>

          <div className="space-y-2">
            <p className="text-sm text-muted-foreground line-clamp-2">
              {entry.sourceText}
            </p>
            <p className="text-sm text-foreground line-clamp-2 font-medium">
              {entry.translatedText}
            </p>
          </div>

          {/* Category and Tags Management */}
          <div className="mt-3 pt-2 border-t border-translator-border/50">
            <EntryManagement
              entry={entry}
              availableCategories={availableCategories}
              availableTags={availableTags}
              onUpdateEntry={onUpdateEntry}
            />
          </div>
        </div>

        <div className="flex items-center gap-1 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-smooth">
          <FeatureTooltip content={TOOLTIP_CONTENT.COPY}>
            <Button
              variant="ghost"
              size="icon"
              onClick={(e) => {
                e.stopPropagation();
                handleCopy(entry.translatedText, "translation");
              }}
              className="h-8 w-8 sm:h-8 sm:w-8 text-muted-foreground hover:text-foreground touch-target"
              aria-label="Copy translation to clipboard"
            >
              <Copy className="h-3 w-3" />
            </Button>
          </FeatureTooltip>

          <FeatureTooltip content={TOOLTIP_CONTENT.FAVORITES}>
            <Button
              variant="ghost"
              size="icon"
              onClick={(e) => {
                e.stopPropagation();
                onToggleFavorite(entry.id);
              }}
              className={`h-8 w-8 sm:h-8 sm:w-8 touch-target ${entry.isFavorite ? 'text-accent' : 'text-muted-foreground hover:text-accent'}`}
              aria-label={entry.isFavorite ? "Remove from favorites" : "Add to favorites"}
            >
              <Star className={`h-3 w-3 ${entry.isFavorite ? 'fill-current' : ''}`} />
            </Button>
          </FeatureTooltip>

          <FeatureTooltip content={TOOLTIP_CONTENT.DELETE}>
            <Button
              variant="ghost"
              size="icon"
              onClick={(e) => {
                e.stopPropagation();
                onDeleteEntry(entry.id);
              }}
              className="h-8 w-8 sm:h-8 sm:w-8 text-muted-foreground hover:text-destructive touch-target"
              aria-label="Delete translation entry"
            >
              <Trash2 className="h-3 w-3" />
            </Button>
          </FeatureTooltip>
        </div>
      </div>
    </div>
  );

  return (
    <div className="bg-translator-panel rounded-lg border border-translator-border shadow-soft">
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-3 sm:p-4 pb-2">
          <TabsList className="grid grid-cols-2 bg-muted rounded-md w-full sm:w-auto">
            <TabsTrigger value="history" className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm">
              <Clock className="h-3 w-3 sm:h-4 sm:w-4" />
              <span className="hidden xs:inline">History</span> ({historyEntries.length})
            </TabsTrigger>
            <TabsTrigger value="favorites" className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm">
              <Star className="h-3 w-3 sm:h-4 sm:w-4" />
              <span className="hidden xs:inline">Saved</span> ({favoriteEntries.length})
            </TabsTrigger>
          </TabsList>

          <div className="flex items-center justify-end gap-1 sm:gap-2 flex-wrap">
            <HelpDialog />
            <ImportExportActions
              entries={entries}
              onExportHistory={exportHistory}
              onImportHistory={importHistory}
            />
            <FeatureTooltip content={TOOLTIP_CONTENT.BULK_SELECT}>
              <Button
                variant={showBulkSelection ? "default" : "outline"}
                size="sm"
                onClick={() => {
                  setShowBulkSelection(!showBulkSelection);
                  setSelectedIds([]);
                }}
                className="text-xs touch-target"
                aria-label={showBulkSelection ? "Disable bulk selection" : "Enable bulk selection"}
              >
                {showBulkSelection ? <CheckSquare className="h-3 w-3 sm:mr-1" /> : <Square className="h-3 w-3 sm:mr-1" />}
                <span className="hidden sm:inline">Bulk Select</span>
              </Button>
            </FeatureTooltip>
          </div>
        </div>

        {/* Search and Filters */}
        <div className="px-4 pb-2">
          <HistoryFilters
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            filters={filters}
            onFiltersChange={setFilters}
            availableLanguages={availableLanguages}
            availableCategories={availableCategories}
            availableTags={availableTags}
          />
        </div>

        <div className="p-4 pt-0">
          {/* Bulk Actions Bar */}
          {showBulkSelection && (
            <BulkActionsBar
              selectedCount={selectedIds.length}
              selectedIds={selectedIds}
              onClearSelection={handleClearSelection}
              onBulkDelete={handleBulkDelete}
              onBulkToggleFavorite={handleBulkToggleFavorite}
              onBulkAddTags={handleBulkAddTags}
              onBulkRemoveTags={handleBulkRemoveTags}
              onBulkSetCategory={handleBulkSetCategory}
              availableCategories={availableCategories}
              availableTags={availableTags}
            />
          )}

          <TabsContent value="history" className="mt-0">
            {showBulkSelection && historyEntries.length > 0 && (
              <div className="mb-4 pb-2 border-b border-translator-border">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleSelectAll(historyEntries)}
                  className="text-xs"
                >
                  {selectedIds.length === historyEntries.length ? "Deselect All" : "Select All"}
                </Button>
              </div>
            )}

            <div className="space-y-3">
              {historyEntries.length > 0 ? (
                historyEntries.map((entry) => (
                  <EntryCard key={entry.id} entry={entry} />
                ))
              ) : searchQuery || Object.keys(filters).length > 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Clock className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p>No translations match your search</p>
                  <p className="text-sm">Try adjusting your search or filters</p>
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <Clock className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p>No translation history yet</p>
                  <p className="text-sm">Your recent translations will appear here</p>
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="favorites" className="mt-0">
            {showBulkSelection && favoriteEntries.length > 0 && (
              <div className="mb-4 pb-2 border-b border-translator-border">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleSelectAll(favoriteEntries)}
                  className="text-xs"
                >
                  {selectedIds.length === favoriteEntries.length ? "Deselect All" : "Select All"}
                </Button>
              </div>
            )}

            <div className="space-y-3">
              {favoriteEntries.length > 0 ? (
                favoriteEntries.map((entry) => (
                  <EntryCard key={entry.id} entry={entry} />
                ))
              ) : searchQuery || Object.keys(filters).length > 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Star className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p>No favorites match your search</p>
                  <p className="text-sm">Try adjusting your search or filters</p>
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <Star className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p>No saved translations yet</p>
                  <p className="text-sm">Star translations to save them here</p>
                </div>
              )}
            </div>
          </TabsContent>
        </div>
      </Tabs>
    </div>
  );
}